import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaDollarSign, FaChartBar } from "react-icons/fa";
import { formatMoment } from "../../../../helper/CommonFunction";
import DashboardContainer from "../../../../components/DashboardContainer";
import renderDataTable from "../../../../helper/renderDataTable";

const DashboardTable = ({
  filteredData,
  pending,
  currentPage,
  setPageLimit,
  setCurrentPage,
  totalPages,
  pageLimit,
  setResetSearch,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Calculate totals
  const totalRevenue = filteredData
    ?.reduce((sum, row) => sum + (parseFloat(row.revenue) || 0), 0)
    .toFixed(2);
  const totalImpressions = filteredData?.reduce(
    (sum, row) => sum + (parseInt(row.impressions) || 0),
    0
  );

  // Header + cell wrappers so column boundaries are visible even without
  // access to renderDataTable's internal customStyles.
  const th = (label) => (
    <span className="px-3 py-2 text-[13px] font-semibold text-black whitespace-nowrap">
      {label}
    </span>
  );

  const td = (value, extraClass = "") => (
    <span
      className={`px-3 py-2 text-[13px] text-[#1C1410] border-r border-[#F5DFC5] last:border-r-0 w-full ${extraClass}`}
    >
      {value ?? "—"}
    </span>
  );

  const column = [
    {
      name: th("Date"),
      selector: (row) => td(formatMoment(row.created_date)),
      minWidth: "130px",
    },
    {
      name: th("App Name"),
      selector: (row) => td(row.app_name),
      minWidth: "150px",
      grow: 1.5,
    },
    {
      name: th("Impressions"),
      selector: (row) => td(row.impressions),
      minWidth: "120px",
    },
    {
      name: th("Fill Rate"),
      selector: (row) => td(row.fill_rate),
      minWidth: "100px",
    },
    {
      name: th("Click"),
      selector: (row) => td(row.click),
      minWidth: "90px",
    },
    {
      name: th("CTR"),
      selector: (row) => td(row.ctr),
      minWidth: "90px",
    },
    {
      name: th("CPM"),
      selector: (row) => td(row.cpm),
      minWidth: "90px",
    },
    {
      name: th("Viewability"),
      selector: (row) => td(row.viewability),
      minWidth: "120px",
    },
    {
      name: th("Revenue($)"),
      selector: (row) => td(row.revenue, "font-semibold"),
      minWidth: "120px",
    },
  ];

  return (
    <DashboardContainer>
      <div className="bg-white border border-[#F0DCC8] rounded-2xl shadow-[0_10px_30px_-18px_rgba(22,34,27,0.25)] w-full overflow-hidden">
        <div className="p-4 sm:p-6 w-full">
          {/* ✅ Totals Display */}
          <div className="flex flex-wrap gap-3 sm:gap-4 mb-5 sm:mb-6">
            <div
              className="flex items-center gap-3 bg-gradient-to-br from-[#FFF4E5] to-[#FEF3E5] border border-[#FDE8CD] px-4 sm:px-6 py-3.5 sm:py-4 flex-1 min-w-[180px] sm:flex-none"
              style={{ borderRadius: "1.4rem 0.5rem 1.4rem 0.5rem" }}
            >
              <span
                className="w-10 h-10 shrink-0 flex items-center justify-center text-[#D97706] text-base mr-1"
                style={{
                  background: "linear-gradient(145deg, #FFFFFF, #FDE8CD)",
                  borderRadius: "58% 42% 55% 45% / 45% 55% 42% 58%",
                }}
              >
                <FaDollarSign />
              </span>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-[#8A7560]">
                  Total Revenue
                </p>
                <p className="text-lg sm:text-xl font-extrabold text-[#1C1410]">
                  ${totalRevenue}
                </p>
              </div>
            </div>

            <div
              className="flex items-center gap-3 bg-gradient-to-br from-[#FFF4E5] to-[#FEF3E5] border border-[#FDE8CD] px-4 sm:px-6 py-3.5 sm:py-4 flex-1 min-w-[180px] sm:flex-none"
              style={{ borderRadius: "0.5rem 1.4rem 0.5rem 1.4rem" }}
            >
              <span
                className="w-10 h-10 shrink-0 flex items-center justify-center text-[#EA580C] text-base mr-1"
                style={{
                  background: "linear-gradient(145deg, #FFFFFF, #FDE8CD)",
                  borderRadius: "58% 42% 55% 45% / 45% 55% 42% 58%",
                }}
              >
                <FaChartBar />
              </span>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-[#8A7560]">
                  Total Impressions
                </p>
                <p className="text-lg sm:text-xl font-extrabold text-[#1C1410]">
                  {totalImpressions.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="py-2 overflow-x-auto">
            {renderDataTable(
              filteredData,
              column,
              pending,
              currentPage,
              setPageLimit,
              setCurrentPage,
              totalPages,
              pageLimit,
              setResetSearch,
              dispatch,
              navigate
            )}
          </div>
        </div>
      </div>
    </DashboardContainer>
  );
};

export default DashboardTable;